import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { notification } from 'antd';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import {
  getConfigurationSchedule,
  getConfigurationScheduleStatus,
  CurriculumScheduleStatus,
} from 'services/api';
import type { SchedulePageParams } from 'services/navigation';
import {
  getCurrentBellScheduleAction,
  getScheduleAction,
  getScheduleErrorsAction,
  scheduleActions,
  scheduleSelectors,
} from '../redux';

export const useScheduleGrid = () => {
  const dispatch = useAppDispatch();

  const { configurationId, scheduleId } = useParams<SchedulePageParams>();

  const scheduleIsLoading = useAppSelector(scheduleSelectors.getScheduleIsLoading);
  const scheduleConstraints = useAppSelector(scheduleSelectors.getConstraints);
  const filters = useAppSelector(scheduleSelectors.getFilters);
  const editMode = useAppSelector(scheduleSelectors.getEditMode);

  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [scheduleProcessing, setScheduleProcessing] = useState(false);

  const scheduleStatusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (configurationId && scheduleId) {
      dispatch(getScheduleAction({ curriculumId: configurationId, scheduleId, filters }));
      dispatch(getCurrentBellScheduleAction());
      dispatch(getScheduleErrorsAction({ configurationId, scheduleId, filters }));
    }
  }, [configurationId, filters, scheduleId]);

  const getScheduleStatus = useCallback(() => {
    if (!configurationId) {
      return;
    }

    if (scheduleStatusIntervalRef.current) {
      clearInterval(scheduleStatusIntervalRef.current);
      scheduleStatusIntervalRef.current = null;
    }

    const fetchStatus = () => {
      getConfigurationScheduleStatus(configurationId).then((res) => {
        setScheduleProcessing(res === CurriculumScheduleStatus.Processing);

        if (res === CurriculumScheduleStatus.None) {
          if (scheduleStatusIntervalRef.current) {
            clearInterval(scheduleStatusIntervalRef.current);
            scheduleStatusIntervalRef.current = null;
          }
          if (configurationId && scheduleId) {
            getConfigurationSchedule(configurationId, scheduleId, filters).then(
              (refinedSchedule) => {
                dispatch(scheduleActions.setSchedule(refinedSchedule.items));
                dispatch(scheduleActions.setHighlightedErrors(null));
                dispatch(getScheduleErrorsAction({ configurationId, scheduleId, filters }));
                notification.success({
                  message: 'Успешно',
                  description: 'Расписание достроено',
                  duration: 1,
                });
              },
            );
          }
        }
      });
    };

    fetchStatus();

    scheduleStatusIntervalRef.current = setInterval(fetchStatus, 5000);
  }, [configurationId, dispatch, filters, scheduleId]);

  const handleRefineSuccess = useCallback(() => {
    if (configurationId && scheduleId) {
      dispatch(getScheduleAction({ curriculumId: configurationId, scheduleId, filters }));
      getScheduleStatus();
    }
  }, [configurationId, dispatch, filters, getScheduleStatus, scheduleId]);

  useEffect(() => {
    return () => {
      if (scheduleStatusIntervalRef.current) {
        clearInterval(scheduleStatusIntervalRef.current);
        scheduleStatusIntervalRef.current = null;
      }
      dispatch(scheduleActions.clearState());
    };
  }, []);

  return {
    state: {
      scheduleIsLoading,
      configurationId,
      scheduleId,
      scheduleConstraints,
      refineModalOpen,
      scheduleProcessing,
      editMode,
    },
    methods: {
      setRefineModalOpen,
      handleRefineSuccess,
    },
  };
};
